import { TFunction } from 'i18next';
import React, { useMemo } from 'react';

import {
  InquirySourceDto,
  PermissionDto,
  ProcessListItemDto,
  ProcessStatusDto,
} from '@/api/generated';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import { CustomTable, CustomTableColumn } from '@/components/table/CustomTable.comp.tsx';
import { CustomTableQuickFilter } from '@/components/table/quickFilters/CustomTableQuickFilter.model.ts';
import {
  daysAgo,
  hasOption,
  isEmptyDate,
  isOption,
  isSameDay,
  withOption,
  withoutOption,
} from '@/components/table/quickFilters/quickFilter.util.ts';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { inquirySourceLabels } from '@/models/inquirySource/InquirySource.model.ts';
import { TableBasicProps } from '@/models/TableBasicProps.model.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { useHasAnyPermission } from '@/utils/usePermissions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetContacts } from '@/views/contacts/utils/useGetContacts.util.ts';
import { useGetCustomers } from '@/views/customers/list/utils/useGetCustomers.util.ts';
import { ProcessesListTableRow } from '@/views/processes/list/components/table/components/ProcessesListTableRow.comp.tsx';
import { ProcessesListSearchFormModel } from '@/views/processes/list/model/ProcessesListSearchForm.model.ts';
import {
  initialProcessesListTableState,
  ProcessesListTableState,
} from '@/views/processes/list/model/ProcessesListTableState.model.ts';
import { useGetProcessesList } from '@/views/processes/list/utils/useGetProcessesList.util.ts';
import { useProcessesListFormValidation } from '@/views/processes/list/utils/useProcessesListForm.validation.ts';

type Props = TableBasicProps<ProcessListItemDto, never> & {
  contactId?: number;
  customerId?: number;
};

const statusesFn = (t: TFunction): SelectSearchOptionModel<ProcessStatusDto>[] => [
  {
    label: t(`processStatusOptions.${ProcessStatusDto.Created}`),
    value: ProcessStatusDto.Created,
  },
  {
    label: t(`processStatusOptions.${ProcessStatusDto.InProgress}`),
    value: ProcessStatusDto.InProgress,
  },
];

export const ProcessesListTable: React.FC<Props> = props => {
  const {
    actionsColumns,
    contactId,
    customerId,
    customRow,
    onCustomAction,
    pageSize,
    rowsPerPageOptions,
    tableKey,
  } = props;
  const { t } = useTranslationWithPrefix('views.processes.table');
  const { getProcesses, isLoading } = useGetProcessesList();
  const validation = useProcessesListFormValidation();
  const { USERS_SHORT } = useDictionariesContext();
  const { getContacts } = useGetContacts();
  const { getCustomers } = useGetCustomers();
  const { processStatusOptions } = useGetSelectOptions();
  const { userSelectOption } = useUserContext();
  const canFilterByUpdateDate = useHasAnyPermission(PermissionDto.FilterProcessesByUpdateDate);
  const { t: dictionaryT } = useTranslationWithPrefix('utils.dictionaries');
  const { t: statisticsT } = useTranslationWithPrefix('views.statistics');

  const inquirySourceOptions: SelectSearchOptionModel<InquirySourceDto>[] = useMemo(
    () =>
      Object.values(InquirySourceDto).map(source => ({
        label: statisticsT(`sourceLabels.${inquirySourceLabels[source]}`),
        value: source,
      })),
    [statisticsT],
  );

  const initialStatuses: SelectSearchOptionModel<ProcessStatusDto>[] = useMemo(
    () => statusesFn(dictionaryT),
    [dictionaryT],
  );

  const handleLoadContacts = async (inputValue: string) => {
    if (!inputValue) {
      return [];
    }
    const { items } = await getContacts({
      page: 0,
      pageSize: 30,
      params: {
        customerFullTextSearchInput: '',
        fullTextSearchInput: inputValue,
        isAlreadyInCustomer: { label: '', value: true },
        isAlreadyInSupplier: null,
        lastContactedFrom: null,
        lastContactedTo: null,
        ownerIds: [],
        status: null,
      },
    });
    return items.map(it => ({
      label: it.lastName + ' ' + it.firstName,
      value: it.contactId,
    }));
  };

  const handleLoadCustomers = async (inputValue: string) => {
    if (!inputValue) {
      return [];
    }
    const { items } = await getCustomers({
      page: 0,
      pageSize: 30,
      params: {
        fullTextSearchInput: inputValue,
        ownerIdOfCustomerContact: null,
      },
    });
    return items.map(it => ({
      label: it.name,
      value: it.customerId,
    }));
  };

  const filters: CustomTableFilterDefinition<ProcessesListSearchFormModel>[] = useMemo(() => {
    const filters: CustomTableFilterDefinition<ProcessesListSearchFormModel>[] = [
      {
        label: t('head.fullTextSearchInput'),
        name: 'fullTextSearchInput',
        type: CustomTableFilterTypeEnum.Text,
        xs: 4,
      },
      {
        label: t('head.createdFrom'),
        name: 'createdFrom',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      {
        label: t('head.createdTo'),
        name: 'createdTo',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      ...(canFilterByUpdateDate
        ? ([
            {
              label: t('head.updatedFrom'),
              name: 'updatedFrom',
              type: CustomTableFilterTypeEnum.Date,
              xs: 2,
            },
            {
              label: t('head.updatedTo'),
              name: 'updatedTo',
              type: CustomTableFilterTypeEnum.Date,
              xs: 2,
            },
          ] as CustomTableFilterDefinition<ProcessesListSearchFormModel>[])
        : []),
      {
        isMulti: true,
        label: t('head.statuses'),
        name: 'statuses',
        options: processStatusOptions,
        type: CustomTableFilterTypeEnum.Select,
        xs: canFilterByUpdateDate ? 3 : 4,
      },
      {
        isMulti: true,
        label: t('head.inquirySources'),
        name: 'inquirySources',
        options: inquirySourceOptions,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
      {
        label: t('head.processCreatorId'),
        name: 'processCreatorId',
        options: USERS_SHORT,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
      {
        label: t('head.processOwnerId'),
        name: 'processOwnerId',
        options: USERS_SHORT,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
    ];
    if (!contactId) {
      filters.push({
        isAsync: true,
        label: t('head.processContactId'),
        loadOptions: handleLoadContacts,
        name: 'processContactId',
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      });
    }
    if (!customerId) {
      filters.push({
        isAsync: true,
        label: t('head.customerId'),
        loadOptions: handleLoadCustomers,
        name: 'customerId',
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      });
    }
    return filters;
  }, [
    t,
    processStatusOptions,
    inquirySourceOptions,
    USERS_SHORT,
    contactId,
    customerId,
    canFilterByUpdateDate,
  ]);

  const quickFilters: CustomTableQuickFilter<ProcessesListSearchFormModel>[] = useMemo(() => {
    const statusOption = (status: ProcessStatusDto) =>
      processStatusOptions.find(option => option.value === status) ?? {
        label: status,
        value: status,
      };
    const createdSince = (days: number) => ({
      apply: () => ({ createdFrom: daysAgo(days), createdTo: null }),
      clear: () => ({ createdFrom: null }),
      group: 'created',
      isActive: (params: ProcessesListSearchFormModel) =>
        isSameDay(params.createdFrom, daysAgo(days)) && isEmptyDate(params.createdTo),
    });
    const statusPill = (status: ProcessStatusDto) => ({
      apply: (params: ProcessesListSearchFormModel) => ({
        statuses: withOption(params.statuses, statusOption(status)),
      }),
      clear: (params: ProcessesListSearchFormModel) => ({
        statuses: withoutOption(params.statuses, status),
      }),
      id: `status-${status}`,
      isActive: (params: ProcessesListSearchFormModel) => hasOption(params.statuses, status),
    });

    return [
      {
        apply: () => ({ processCreatorId: null, processOwnerId: userSelectOption }),
        clear: () => ({ isNeedingAttention: undefined, processOwnerId: null }),
        group: 'who',
        id: 'mine',
        isActive: params => isOption(params.processOwnerId, userSelectOption.value),
        label: t('quickFilters.mine'),
      },
      {
        apply: () => ({
          isNeedingAttention: undefined,
          processCreatorId: userSelectOption,
          processOwnerId: null,
        }),
        clear: () => ({ processCreatorId: null }),
        group: 'who',
        id: 'createdByMe',
        isActive: params => isOption(params.processCreatorId, userSelectOption.value),
        label: t('quickFilters.createdByMe'),
      },
      {
        apply: () => ({
          isNeedingAttention: true,
          processCreatorId: null,
          processOwnerId: userSelectOption,
        }),
        clear: () => ({ isNeedingAttention: undefined }),
        id: 'needsAction',
        isActive: params => params.isNeedingAttention === true,
        label: t('quickFilters.needsAction'),
        tooltip: t('quickFilters.needsActionTooltip'),
      },
      { ...createdSince(0), id: 'today', label: t('quickFilters.today') },
      { ...createdSince(6), id: 'last7Days', label: t('quickFilters.last7Days') },
      { ...statusPill(ProcessStatusDto.Created), label: t('quickFilters.statusNew') },
      { ...statusPill(ProcessStatusDto.InProgress), label: t('quickFilters.inProgress') },
      { ...statusPill(ProcessStatusDto.Finished), label: t('quickFilters.finished') },
      { ...statusPill(ProcessStatusDto.Canceled), label: t('quickFilters.canceled') },
    ];
  }, [processStatusOptions, t, userSelectOption]);

  const tableHead: CustomTableColumn<string>[] = useMemo(() => {
    const head = [
      {
        id: 'name',
        label: t('body.name'),
        sx: { width: '27%' },
      },
      {
        id: 'customer',
        label: t('body.customer'),
        sx: { width: '20%' },
      },
      {
        id: 'owner',
        label: t('body.owner'),
        sx: { width: '9%' },
      },
      {
        id: 'creator',
        label: t('body.creator'),
        sx: { width: '9%' },
      },
      {
        id: 'status',
        label: t('body.status'),
        sx: { width: '11%' },
      },
      {
        id: 'createdAt',
        label: t('body.createdAt'),
        sx: { width: '14%' },
      },
    ];
    if (actionsColumns) {
      head.push({
        id: 'actions',
        label: '',
        sx: { width: '5%' },
      });
    }
    return head;
  }, [actionsColumns, t]);

  const init = async (initialState: ProcessesListTableState) => {
    let parsedState = initialState;
    if (customerId) {
      parsedState = {
        ...initialState,
        params: {
          ...initialState.params,
          customerId: { label: '', value: customerId },
        },
      };
    }
    if (contactId) {
      parsedState = {
        ...initialState,
        params: {
          ...initialState.params,
          processContactId: { label: '', value: contactId },
        },
      };
    }
    return await getProcesses(parsedState);
  };

  return (
    <CustomTable<string, ProcessesListSearchFormModel, ProcessListItemDto>
      CustomTableRow={customRow ?? ProcessesListTableRow}
      customTableRowAction={onCustomAction}
      expandableRows
      filters={filters}
      getTableDataRequest={getProcesses}
      idKey="processId"
      init={init}
      initParams={{
        ...initialProcessesListTableState,
        pageSize: pageSize ?? initialProcessesListTableState.pageSize,
        params: {
          ...initialProcessesListTableState.params,
          customerId: customerId ? { label: '', value: customerId } : null,
          processContactId: contactId ? { label: '', value: contactId } : null,
          processOwnerId: userSelectOption,
          statuses: initialStatuses,
        },
      }}
      isLoading={isLoading}
      quickFilters={quickFilters}
      rowsPerPageOptions={rowsPerPageOptions}
      size="small"
      tableHead={tableHead}
      tableKey={tableKey}
      validation={validation}
    />
  );
};
